import React, { useState, useEffect } from 'react';
import { authService } from '../../api';
import './Connexion.css';
import { useNavigate, Link, useLocation } from 'react-router-dom';

const Connexion = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const [submitError, setSubmitError] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [userEmailFromLink, setUserEmailFromLink] = useState('');
  const [userNameFromLink, setUserNameFromLink] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    password: false
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Lire les paramètres d'URL
    const searchParams = new URLSearchParams(location.search);
    const messageFromUrl = searchParams.get('message');
    const emailFromUrl = searchParams.get('user_email');
    const nameFromUrl = searchParams.get('user_name');
    
    if (messageFromUrl) {
      setWelcomeMessage(decodeURIComponent(messageFromUrl));
    }
    
    if (emailFromUrl) {
      setUserEmailFromLink(decodeURIComponent(emailFromUrl));
      // Pré-remplir l'email si présent dans l'URL
      setFormData(prev => ({
        ...prev,
        email: decodeURIComponent(emailFromUrl)
      }));
    }
    
    if (nameFromUrl) {
      setUserNameFromLink(decodeURIComponent(nameFromUrl));
    }
    
    // Message depuis state (si navigation React)
    if (location.state?.message) {
      setWelcomeMessage(location.state.message);
    }
    
    if (location.state?.email) {
      setUserEmailFromLink(location.state.email);
    }
    
    // Effacer le message après 15 secondes
    const timer = setTimeout(() => {
      setWelcomeMessage('');
    }, 15000);
    
    return () => clearTimeout(timer);
  }, [location]);

  useEffect(() => {
    // Valider chaque champ touché
    const newErrors = {};
    Object.keys(touched).forEach(field => {
      if (touched[field]) {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
        }
      }
    });
    setErrors(newErrors);
  }, [formData, touched]);

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'email':
        if (!value) {
          error = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Veuillez entrer un email valide';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Le mot de passe est requis';
        } else if (value.length < 6) {
          error = 'Le mot de passe doit contenir au moins 6 caractères';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error || undefined
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    const error = validateField(name, formData[name]);
    setErrors(prev => ({
      ...prev,
      [name]: error || undefined
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitError('');

  // Marquer tous les champs comme touchés avant la soumission
  const newTouched = {
    email: true,
    password: true
  };
  setTouched(newTouched);

  // Valider tous les champs
  const newErrors = {};
  let isValid = true;

  Object.keys(formData).forEach(field => {
    const error = validateField(field, formData[field]);
    if (error) {
      newErrors[field] = error;
      isValid = false;
    }
  });

  setErrors(newErrors);

  if (!isValid) return;

  try {
    const response = await authService.login(formData);

    // Stockage du token et des données utilisateur
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    
    // SUPPRIMER cette vérification inutile
    const searchParams = new URLSearchParams(location.search);
    const isEmailVerification = searchParams.has('message') || 
                               location.state?.message?.includes('vérifier');
    
    // IMPORTANT: Toujours rediriger vers l'accueil après connexion réussie
    // Si l'email n'est pas vérifié, l'API devrait gérer cela différemment
    
    // Redirection normale en fonction du rôle
    if (response.user.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/'); // TOUJOURS rediriger vers l'accueil après connexion
    }
    
  } catch (err) {
    // Si l'erreur concerne un email non vérifié, on peut gérer cela différemment
    if (err.message.includes('non vérifié') || err.message.includes('email_verified')) {
      // Stocker quand même les informations si nécessaire
      localStorage.setItem('pending_email', formData.email);
      
      // Rediriger vers la page d'attente de vérification
      navigate('/inscription/en-attente-verification', {
        state: {
          email: formData.email,
          message: 'Veuillez vérifier votre email pour activer votre compte.'
        }
      });
    } else {
      setSubmitError(err.message || 'Une erreur est survenue lors de la connexion');
    }
  }
};

  return (
    <div className="login-wrapper">
      <Link to="/" className="home-arrow">
        <div className="arrow-icon">←</div>
      </Link>
      <div className="login-card">
        <h2 className="login-title">Connexion à votre compte</h2>
        
        {welcomeMessage && (
          <div className="welcome-message-container">
            <div className="welcome-icon">👋</div>
            <div className="welcome-message">
              <p><strong>Bonjour {userNameFromLink || 'nouvel utilisateur'} !</strong></p>
              <p>{welcomeMessage}</p>
              {userEmailFromLink && (
                <p className="user-email-info">
                  Email : <strong>{userEmailFromLink}</strong>
                </p>
              )}
            </div>
          </div>
        )}
        
        {submitError && <div className="login-error">{submitError}</div>}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className={`input-field ${errors.email ? 'has-error' : ''}`}>
            <label className="input-label">Adresse email *</label>
            <input
              type="email"
              name="email"
              className="login-input"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>
          
          <div className={`input-field ${errors.password ? 'has-error' : ''}`}>
            <label className="input-label">Mot de passe *</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="login-input password-input"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <svg className="eye-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9M12,4.5C17,4.5 21.27,7.61 23,12C21.27,16.39 17,19.5 12,19.5C7,19.5 2.73,16.39 1,12C2.73,7.61 7,4.5 12,4.5M3.18,12C4.83,15.36 8.24,17.5 12,17.5C15.76,17.5 19.17,15.36 20.82,12C19.17,8.64 15.76,6.5 12,6.5C8.24,6.5 4.83,8.64 3.18,12Z" />
                  </svg>
                ) : (
                  <svg className="eye-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M11.83,9L15,12.16C15,12.11 15,12.05 15,12A3,3 0 0,0 12,9C11.94,9 11.89,9 11.83,9M7.53,9.8L9.08,11.35C9.03,11.56 9,11.77 9,12A3,3 0 0,0 12,15C12.22,15 12.44,14.97 12.65,14.92L14.2,16.47C13.53,16.8 12.79,17 12,17A5,5 0 0,1 7,12C7,11.21 7.2,10.47 7.53,9.8M2,4.27L4.28,6.55L4.73,7C3.08,8.3 1.78,10 1,12C2.73,16.39 7,19.5 12,19.5C13.55,19.5 15.03,19.2 16.38,18.66L16.81,19.08L19.73,22L21,20.73L3.27,3M12,7A5,5 0 0,1 17,12C17,12.64 16.87,13.26 16.64,13.82L19.57,16.75C21.07,15.5 22.27,13.86 23,12C21.27,7.61 17,4.5 12,4.5C10.6,4.5 9.26,4.75 8,5.2L10.17,7.35C10.74,7.13 11.35,7 12,7Z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>
          
          <button type="submit" className="login-button">Se connecter</button>
        </form>
        
        <div className="login-footer">
          <p>Pas encore membre ? 
            <button 
              className="login-linkbtn" 
              onClick={() => navigate('/inscription')}
            >
              Créer un compte
            </button>
          </p>
          <p>
            <button 
              className="login-linkbtn"
              onClick={() => navigate('/mot-de-passe-oublie')}
              style={{ fontSize: '14px', marginTop: '10px' }}
            >
              Mot de passe oublié ?
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Connexion;